import { useQuery } from '@tanstack/react-query';
import { getAbiItem, getAddress, type Address } from 'viem';
import { usePublicClient, useReadContracts } from 'wagmi';
import { referralManagerAbi } from '@syntra/abi';
import { DEPLOYMENT as D, INDEXER_URL } from '../config/env.ts';
import { pagedLogs } from '../lib/logs.ts';

export interface TeamMember {
  address: Address;
  side: number; // 0 left, 1 right
  rank: number;
  activeUsd: bigint;
  /** False while the member has joined but not staked: it takes its slot with its first stake. */
  placed: boolean;
}

const ZERO = '0x0000000000000000000000000000000000000000';

/**
 * Everyone `sponsor` invited, split into the members who hold a slot in the tree and those who have joined but not
 * staked yet. The list comes from the indexer or, without one, from the ReferralManager's Bound logs; each member's
 * place, rank and volume are read from the contract so the card never lags the chain.
 */
export function useTeam(sponsor: Address | undefined) {
  const client = usePublicClient();
  const members = useQuery({
    queryKey: ['team', sponsor],
    enabled: Boolean(sponsor && client),
    staleTime: 30_000,
    refetchInterval: 30_000,
    queryFn: async (): Promise<Address[]> => {
      if (INDEXER_URL) {
        const res = await fetch(`${INDEXER_URL}/accounts/${sponsor}`).catch(() => null);
        if (res?.ok) {
          const body = (await res.json()) as { directReferrals: { address: string }[] };
          return body.directReferrals.map((r) => getAddress(r.address));
        }
      }
      const logs = await pagedLogs(await client!.getBlockNumber(), BigInt(D.deployedAtBlock ?? 0), (fromBlock, toBlock) =>
        client!.getLogs({ address: D.referral, event: getAbiItem({ abi: referralManagerAbi, name: 'Bound' }), args: { sponsor }, fromBlock, toBlock }),
      );
      return logs.flatMap((l) => (l.args.user ? [getAddress(l.args.user)] : []));
    },
  });
  const list = members.data ?? [];
  const nodes = useReadContracts({
    allowFailure: false,
    contracts: list.map((a) => ({ address: D.referral, abi: referralManagerAbi, functionName: 'node', args: [a] }) as const),
    query: { enabled: list.length > 0, refetchInterval: 15_000 },
  });
  const team: TeamMember[] = list.map((address, i) => {
    const n = nodes.data?.[i] as { parent: Address; side: number; rank: number; ownActiveUsd: bigint } | undefined;
    return { address, side: n?.side ?? 0, rank: n?.rank ?? 0, activeUsd: n?.ownActiveUsd ?? 0n, placed: Boolean(n && n.parent !== ZERO) };
  });
  return {
    placed: team.filter((m) => m.placed),
    pending: team.filter((m) => !m.placed),
    loading: members.isPending || (list.length > 0 && nodes.isPending),
  };
}
